<template>
  <div
    v-if="modelValue"
    class="modal fade show"
    tabindex="-1"
    style="display: block; background-color: rgba(0, 0, 0, 0.5)"
    @click.self="modelValue = false"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Add New EAC Premium</h5>
          <button
            type="button"
            class="btn-close"
            @click="modelValue = false"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleSubmit">
            <div class="mb-3">
              <label for="premiumCategory" class="form-label">Category *</label>
              <select
                id="premiumCategory"
                v-model="formData.category"
                class="form-select"
                required
              >
                <option value="">Select Category</option>
                <option
                  v-for="category in availableCategories"
                  :key="category"
                  :value="category"
                >
                  {{ category.replace("_", " ").toUpperCase() }}
                </option>
                <option value="__custom__">+ Add New Category</option>
              </select>
            </div>

            <!-- Custom category input -->
            <div v-if="formData.category === '__custom__'" class="mb-3">
              <label for="customCategory" class="form-label"
                >New Category *</label
              >
              <input
                id="customCategory"
                v-model="formData.custom_category"
                type="text"
                class="form-control"
                required
                placeholder="e.g. technology, ecolabel"
              />
            </div>

            <div class="mb-3">
              <label for="premiumType" class="form-label">Premium Type *</label>
              <select
                id="premiumType"
                v-model="formData.premium_type"
                class="form-select"
                required
              >
                <option value="">Select Premium Type</option>
                <option
                  v-for="type in availableTypesForCategory"
                  :key="type"
                  :value="type"
                >
                  {{ type }}
                </option>
                <option value="__custom__">+ Add New Premium Type</option>
              </select>
            </div>

            <!-- Custom premium type input -->
            <div v-if="formData.premium_type === '__custom__'" class="mb-3">
              <label for="customPremiumType" class="form-label"
                >New Premium Type *</label
              >
              <input
                id="customPremiumType"
                v-model="formData.custom_premium_type"
                type="text"
                class="form-control"
                required
                placeholder="e.g. SOLAR, WIND, EKO_ENERGY"
              />
            </div>

            <div class="mb-3">
              <label for="premiumValue" class="form-label"
                >Premium Value (€/MWh) *</label
              >
              <input
                id="premiumValue"
                v-model.number="formData.premium_value"
                type="number"
                step="0.0001"
                class="form-control"
                required
                min="0"
              />
            </div>

            <div class="mb-3">
              <label for="premiumDate" class="form-label">Date</label>
              <input
                id="premiumDate"
                v-model="formData.date"
                type="date"
                class="form-control"
              />
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="modelValue = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="handleSubmit"
            :disabled="loading"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            Add Premium
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { EacPremium } from "@/market_insights/admin/eacPricingTypes";

interface NewPremium {
  category: string;
  custom_category?: string;
  premium_type: string;
  custom_premium_type?: string;
  premium_value: number;
  date: string;
}

const props = defineProps<{
  premiumHistory: EacPremium[];
}>();

const emit = defineEmits<{
  "premium-added": [];
}>();

const modelValue = defineModel<boolean>({ required: true });

// Global handlers
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

// State
const loading = ref(false);
const formData = ref<NewPremium>({
  category: "",
  custom_category: "",
  premium_type: "",
  custom_premium_type: "",
  premium_value: 0,
  date: new Date().toISOString().split("T")[0],
});

// Computed properties
const availableCategories = computed(() => {
  const categories = [
    ...new Set(props.premiumHistory.map((premium) => premium.category)),
  ].sort();
  return categories;
});

const availableTypes = computed(() => {
  const types = [
    ...new Set(props.premiumHistory.map((premium) => premium.premium_type)),
  ].sort();
  return types;
});

const availableTypesForCategory = computed(() => {
  if (!formData.value.category || formData.value.category === "__custom__") {
    return availableTypes.value;
  }

  const types = [
    ...new Set(
      props.premiumHistory
        .filter((premium) => premium.category === formData.value.category)
        .map((premium) => premium.premium_type)
    ),
  ].sort();
  return types;
});

// Reset form when modal closes
watch(modelValue, (isOpen) => {
  if (!isOpen) {
    resetForm();
  }
});

// Clear premium type when category changes
watch(
  () => formData.value.category,
  () => {
    formData.value.premium_type = "";
    formData.value.custom_premium_type = "";
  }
);

const resetForm = () => {
  formData.value = {
    category: "",
    custom_category: "",
    premium_type: "",
    custom_premium_type: "",
    premium_value: 0,
    date: new Date().toISOString().split("T")[0],
  };
};

const handleSubmit = async () => {
  loading.value = true;
  try {
    // Prepare the payload, using custom values if selected
    const premiumData = {
      ...formData.value,
      category:
        formData.value.category === "__custom__"
          ? formData.value.custom_category
          : formData.value.category,
      premium_type:
        formData.value.premium_type === "__custom__"
          ? formData.value.custom_premium_type
          : formData.value.premium_type,
    };

    // Remove the custom fields as they're not needed in the API
    delete premiumData.custom_category;
    delete premiumData.custom_premium_type;

    await goSolidApi.post("/api/admin/eac-pricing/premiums", premiumData);
    globalSuccessHandler("Success", "EAC premium added successfully");
    modelValue.value = false;
    emit("premium-added");
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    loading.value = false;
  }
};
</script>
